import Fill from 'ol/style/Fill';
import { replaceTextPlaceholder } from '/src/modules/language/language';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import { getCenter as getExtentCenter } from 'ol/extent';
import { hexToRgbA, calculateExtent } from '/src/modules/misc/misc';
import Style from 'ol/style/Style';
import Polygon from 'ol/geom/Polygon';
import { stringDivider, createBoothDimensionText, defineFeatureStyle, dividerInt, createBoothNumberTextPoint, calculateSidesForBoothDimension, koelnPseudoPolygon } from '/src/modules/features/featureStyle';
import { exhibitionConfig, map } from '/main';
import { lang, showOnlyNischenstaende } from '../../../main';
import { fitTextToBox, screenBoxFromGeometry } from './responsiveText';

/**
 * Function for styling a polygon
 * @param {object} feature      The feature to be styled
 * @param {number} resolution   The current resolution
 * @return {Array}              The resulting styles array
 */
export function stylePolygon(feature, resolution) {
  let styleArray = [];
  let textObject;
  let numberObject;
  let textStyle;
  let text;
  let style;
  let polygonFontSize = 12;

  const featureStyle = defineFeatureStyle(feature);
  const featureMainExhibitor = feature.get('mainExhibitor');
  const featureNumber = typeof feature.get('number') !== 'undefined' ? feature.get('number') : null;
  const isNischenstand = feature.get('isNischenstand') === true || feature.get('isNischenstand') === 'true';
  const isSold = feature.get('isSold') === true || feature.get('isSold') === 'true';

  if (typeof feature.get('name') !== 'undefined') {
    let featureName = feature.get('name');

    featureName = replaceTextPlaceholder(featureName);

    text = stringDivider(featureName, dividerInt, '\n');
  } else {
    text = '';
  }

  style = new Style({
    stroke: new Stroke({
      color: featureStyle.general?.stroke ?? '#000000',
      width: featureStyle.general?.strokeWidth ?? 1,
      opacity: featureStyle.general?.strokeOpacity ?? 1,
    }),
  });

  if (featureStyle['text'] !== null) {
    if (featureStyle['text']['maxResolution'] > resolution) {
      let numberInCenter = false;
      if (feature.get('type') === 'Stand') {
        if ((typeof feature.get('width') !== 'undefined' && feature.get('width') > 0) && (typeof feature.get('depth') !== 'undefined' && feature.get('depth') > 0)) {
          let sides = calculateSidesForBoothDimension(feature);
          let dimensions;

          if (sides !== undefined) {
            let shortestSide = sides[0];
            let longestSide = sides[1];

            dimensions = createBoothDimensionText(feature, shortestSide, longestSide, false);
          } else {
            dimensions = createBoothDimensionText(feature, false, false, false);
          }

          styleArray.push(dimensions[0]);
          styleArray.push(dimensions[1]);
        }

        let middleText = '';
        let numberInCenter = false;
        // let nischenText = '';

        const hasValue = (value) => value !== undefined && value !== null && value !== '';
        const mainName = featureMainExhibitor?.[0]?.name ?? null;

        if (showOnlyNischenstaende === true) {
          if (isNischenstand && isSold) {
            // nischenText = stringDivider(lang['SOLD'], dividerInt, '\n');

            if (mainName !== null && featureMainExhibitor?.length > 0) {
              middleText = stringDivider(mainName, dividerInt, '\n');
            } /* else {
                middleText = nischenText;
              } */
          } else if (isNischenstand && !isSold) {
            // nischenText = stringDivider(lang['FREE'], dividerInt, '\n');

            if (mainName !== null && featureMainExhibitor?.length > 0) {
              middleText = stringDivider(mainName, dividerInt, '\n');
            } /* else {
                middleText = nischenText;
              } */
          } else {
            if (mainName !== null && featureMainExhibitor?.length > 0) {
              middleText = stringDivider(mainName, dividerInt, '\n');
            } else if (hasValue(featureNumber)) {
              middleText = ' ' + featureNumber;
              numberInCenter = true;
            } else {
              middleText = '';
            }
          }
        } else {
          if (mainName !== null && featureMainExhibitor?.length > 0) {
            middleText = stringDivider(mainName, dividerInt, '\n');
          } else if (hasValue(featureNumber)) {
            middleText = ' ' + featureNumber;
            numberInCenter = true;
          } else {
            middleText = '';
          }
        }

        const minFontPx = 8;
        const maxFontPx = Number(polygonFontSize);
        const fontFamily = 'sans-serif';
        const fontWeight = 'normal';
        const labelPaddingPx = 10;

        const core = exhibitionConfig?.core;
        let labelGeometry;
        if (core?.fairground === 'koeln') {
          labelGeometry = koelnPseudoPolygon(feature, map.getView().getRotation());
        } else {
          labelGeometry = createPseudoPolygon(feature, false);
        }

        const box = screenBoxFromGeometry(map, labelGeometry);
        let maxWpx = Math.max(0, box.widthPx - 2 * labelPaddingPx);
        let maxHpx = Math.max(0, box.heightPx - 2 * labelPaddingPx);

        let extraRotationRad = 0;

        if (core?.fairground !== 'koeln') {
          let rotateText = true;
          map.getLayers().forEach(function (layer) {
            if (
              layer.get('name') == 'Freigelände Nord' ||
              layer.get('name') == 'Freigelände 3-8' ||
              layer.get('name') == 'Freigelände 4-8' ||
              layer.get('name') == 'Freigelände 9-13' ||
              layer.get('name') == 'Atrium'
            ) {
              rotateText = false;
            }
          });

          if (rotateText === true) {
            extraRotationRad = 90 * (Math.PI / 180);
          }
        }

        let responsiveTextCandidate = '';

        if (isNischenstand === true) {
          if (mainName !== null && featureMainExhibitor?.length > 0) {
            responsiveTextCandidate = String(mainName);
          } else if (hasValue(featureNumber)) {
            responsiveTextCandidate = ' ' + featureNumber;
          } else {
            responsiveTextCandidate = '';
          }
        }

        let fitted = null;

        if (isNischenstand === true && responsiveTextCandidate) {
          fitted = fitTextToBox({
            text: responsiveTextCandidate,
            maxW: maxWpx,
            maxH: maxHpx,
            fontFamily,
            fontWeight,
            maxFontPx,
            minFontPx,
            angleRad: extraRotationRad,
          });
        }

        if (middleText == 'HANVAC') {
          console.debug('fitted:', fitted);
        }

        textObject = new Text({
          font: polygonFontSize + 'px sans-serif',
          fill: new Fill({
            color: featureStyle['text']['color'],
          }),
          text: middleText,
          overflow: false,
          rotateWithView: (core?.fairground === 'koeln') ? false : true,
        });

        if (fitted) {
          textObject.setText(fitted.resultText);
          textObject.setFont(`${fitted.fontPx}px ${fontFamily}`);
          textObject.setOverflow(true);

          if (middleText == 'HANVAC') {
            console.debug('textObject text:', textObject.getText());
            console.debug('textObject font:', textObject.getFont());
            console.debug('textObject overflow:', textObject.getOverflow());
          }
        }

        if (core?.fairground === 'koeln') { } else {
          if (extraRotationRad !== 0) {
            textObject.setRotation(extraRotationRad);
          }
        }


        if (core?.faiground === 'koeln') {
          textStyle = new Style({
            geometry: function (feature) {
              return koelnPseudoPolygon(feature, map.getView().getRotation());
            },
          });
        } else {
          textStyle = new Style({
            geometry: function (feature) {
              return createPseudoPolygon(feature, false);
            },
          });
        }
        textStyle.setText(textObject);

        if (isNischenstand === true && responsiveTextCandidate && !fitted) {
          textStyle.setText(null);
        }

        styleArray.push(textStyle);

        if (featureStyle['text'] !== null && featureStyle['text']['stroke'] !== null) {
          style.setStroke(new Stroke({
            color: featureStyle['text']['stroke'],
            width: featureStyle['text']['strokeWidth'],
            opacity: featureStyle['text']['strokeOpacity'],
          }));
        }

        if (numberInCenter === false) {
          if (typeof feature.get('number') !== 'undefined') {
            numberObject = createBoothNumberTextPoint(feature, featureNumber, polygonFontSize - 2, featureStyle, false);

            if (featureStyle['number']['backgroundFill'] !== null) {
              numberObject.setBackgroundFill(new Fill({
                color: featureStyle['number']['backgroundFill'],
              }));
            }
            styleArray.push(numberObject);
          }
        }
      } else if (feature.get('type') !== 'Stand') {
        textObject = new Text({
          font: 50 / resolution + 'px sans-serif',
          fill: new Fill({
            color: hexToRgbA(featureStyle['text']['color'], 0.8),
          }),
          text: text,
          overflow: false,
          rotateWithView: true,
        });

        if (featureStyle['text'] !== null && featureStyle['text']['stroke'] !== null) {
          textObject.setStroke(new Stroke({
            color: featureStyle['text']['stroke'],
            width: featureStyle['text']['strokeWidth'],
            opacity: featureStyle['text']['strokeOpacity'],
          }));
        }
      } else {
        textObject = new Text({
          font: polygonFontSize + 'px sans-serif',
          fill: new Fill({
            color: featureStyle['text']['color'],
          }),
          text: text,
          overflow: false,
          rotateWithView: false,
        });

        if (featureStyle['text'] !== null && featureStyle['text']['stroke'] !== null) {
          textObject.setStroke(new Stroke({
            color: featureStyle['text']['stroke'],
            width: featureStyle['text']['strokeWidth'],
            opacity: featureStyle['text']['strokeOpacity'],
          }));
        }
      }
    }
  }

  style.setFill(new Fill({
    color: hexToRgbA(featureStyle['general']['fill'], featureStyle['general']['fillOpacity']),
  }));

  if (feature.get('type') === 'Stand') {
    if (resolution > 0.4) {
      style.getFill().setColor(hexToRgbA(featureStyle['general']['fill'], 0.5));
    } else {
      style.getFill().setColor(hexToRgbA(featureStyle['general']['fill'], featureStyle['general']['fillOpacity']));
    }
  }
  styleArray.push(style);

  return styleArray;
}

/**
 * Create a pseudo polygon out of a feature for correct text positioning
 * Use in OL geometry function
 * @param {object} feature
 * @param {boolean} rotate
 * @return {Polygon}
 */
export function createPseudoPolygon(feature, rotate) {
  if (typeof feature.get('pseudoPolygon') !== 'undefined') {
    return feature.get('pseudoPolygon');
  } else {
    let pseudoPolygon;

    if (feature.getGeometry().getCoordinates(false)[0].length > 5) {
      pseudoPolygon = new Polygon([calculateExtent(feature.getGeometry().getCoordinates(false)[0])]);
      if (rotate === true) {
        pseudoPolygon.rotate(90 * (Math.PI / 180), getExtentCenter(pseudoPolygon.getExtent()));
      }
    } else {
      pseudoPolygon = new Polygon(feature.getGeometry().getCoordinates(false));
      if (rotate === true) {
        pseudoPolygon.rotate(90 * (Math.PI / 180), getExtentCenter(feature.getGeometry().getExtent()));
      }
    }
    feature.set('pseudoPolygon', pseudoPolygon);
    return pseudoPolygon;
  }
}
